import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { X, Check } from 'lucide-react';

interface AreaSelectorProps {
  screenshot: string;
  onConfirm: (croppedImage: string) => void;
  onCancel: () => void;
}

interface SelectionRect {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export const AreaSelector: React.FC<AreaSelectorProps> = ({ screenshot, onConfirm, onCancel }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load and draw the screenshot
  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    image.onload = () => {
      // Set canvas size to match container
      const container = containerRef.current;
      if (container) {
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;
        
        // Draw the screenshot
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      }
    };

    image.src = screenshot;
  }, [screenshot]);

  // Redraw canvas with selection rectangle
  const redrawCanvas = useCallback((rect: SelectionRect | null) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear and redraw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    if (rect) {
      const x = Math.min(rect.startX, rect.endX);
      const y = Math.min(rect.startY, rect.endY);
      const width = Math.abs(rect.endX - rect.startX);
      const height = Math.abs(rect.endY - rect.startY);

      // Draw semi-transparent overlay outside selection
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, y); // Top
      ctx.fillRect(0, y, x, height); // Left
      ctx.fillRect(x + width, y, canvas.width - (x + width), height); // Right
      ctx.fillRect(0, y + height, canvas.width, canvas.height - (y + height)); // Bottom

      // Draw selection rectangle border
      ctx.strokeStyle = '#2055FF';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);

      // Draw corner handles
      const handleSize = 8;
      ctx.fillStyle = '#2055FF';
      ctx.fillRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
      ctx.fillRect(x + width - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
      ctx.fillRect(x - handleSize / 2, y + height - handleSize / 2, handleSize, handleSize);
      ctx.fillRect(x + width - handleSize / 2, y + height - handleSize / 2, handleSize, handleSize);
    }
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);
    setSelection({
      startX: x,
      startY: y,
      endX: x,
      endY: y,
    });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setSelection((prev) => {
      if (!prev) return null;
      const updated = { ...prev, endX: x, endY: y };
      redrawCanvas(updated);
      return updated;
    });
  }, [isDrawing, redrawCanvas]);

  const handleMouseUp = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selection) return;

    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    // Calculate selection bounds
    const x = Math.min(selection.startX, selection.endX);
    const y = Math.min(selection.startY, selection.endY);
    const width = Math.abs(selection.endX - selection.startX);
    const height = Math.abs(selection.endY - selection.startY);

    // Ensure minimum size
    if (width < 50 || height < 50) {
      alert('Selection area is too small. Please select a larger area.');
      return;
    }

    // Calculate scale factors (canvas display size vs actual image size)
    const scaleX = image.naturalWidth / canvas.width;
    const scaleY = image.naturalHeight / canvas.height;

    // Create a new canvas for the cropped image
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = width * scaleX;
    cropCanvas.height = height * scaleY;
    const cropCtx = cropCanvas.getContext('2d');

    if (cropCtx) {
      // Draw the cropped portion from the original image
      cropCtx.drawImage(
        image,
        x * scaleX,
        y * scaleY,
        width * scaleX,
        height * scaleY,
        0,
        0,
        cropCanvas.width,
        cropCanvas.height
      );

      // Convert to data URL
      const croppedDataUrl = cropCanvas.toDataURL('image/png');
      onConfirm(croppedDataUrl);
    }
  }, [selection, onConfirm]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>
          Select area to analyze
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            startIcon={<X size={16} />}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selection}
            startIcon={<Check size={16} />}
            sx={{
              backgroundColor: '#2055FF',
              '&:hover': {
                backgroundColor: '#1a45d5',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Confirm
          </Button>
        </Box>
      </Box>

      {/* Canvas Container */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'crosshair',
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
        <img
          ref={imageRef}
          src={screenshot}
          alt="Screenshot"
          style={{ display: 'none' }}
        />
      </Box>

      {/* Instructions */}
      <Box
        sx={{
          padding: '12px 24px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '14px',
          textAlign: 'center',
        }}
      >
        Click and drag to select the area you want to analyze
      </Box>
    </Box>
  );
};

